import * as React from 'react'
import * as cn from 'classnames'
import * as csm from '../../utils/csm'
import fireDraperTrackingEvent from '../../utils/draper'
import * as styles from './PrerollPlayer.module.scss'
import { mutedButton, unmutedButton } from './MuteButtons'
import fetch from '../../utils/fetch'
import { ItemPrerolls, safeBool } from 'DVWebNodeUtils';
import { VideoContextType } from '../../utils/videoContext'

const LOAD_ERROR_TIMEOUT = 4000
const PLAYER_COUNTER_PREFIX = 'DVWeb::Storefront::PreRollPlayer'

interface PrerollPlayerParentListeners {
  onPlayStart?: () => void
  onPlayStop?: () => void
  onPlayComplete?: () => void
}

interface PrerollPlayerProps {
  prerolls: ItemPrerolls
  link?: string
  placement: PrerollPlayerPlacement
  parentListeners?: PrerollPlayerParentListeners
  videoContext: VideoContextType
  playerIsActive: boolean // active or passive playback (e.g user initiated?)
}

interface PrerollPlayerState {
  playing: boolean
  originalVolume?: number
  hasPlayed: boolean
  hasError: boolean
  hidePlayer: boolean
  defaultVolume?: number
  paused: boolean
}

export const enum PrerollPlayerPlacement {
  Superhero,
  Packshot
}

export default class PrerollPlayer extends React.PureComponent<PrerollPlayerProps, PrerollPlayerState> {
  playHandler: () => void
  stopHandler: () => void
  updateHandler: () => void
  errorHandler: () => void
  waitingHandler: () => void
  scrollHandler: () => void
  firstQuartileReached: boolean
  midpointReached: boolean
  thirdQuartileReached: boolean
  expectedReadyState: number

  wrappers: {
    videoWrapper?: HTMLAnchorElement
    videoPlayer?: HTMLVideoElement
  } = {}

  constructor(props: PrerollPlayerProps) {

    super(props)
    this.state = {
      hasError: false,
      playing: false,
      hidePlayer: true,
      hasPlayed: false,
      paused: false
    }
    this.playHandler = null
    this.stopHandler = null
    this.updateHandler = null

    this.firstQuartileReached = false
    this.midpointReached = false
    this.thirdQuartileReached = false
    this.expectedReadyState = 4
  }

  styleFromPlacementType() {
    switch(this.props.placement) {
      case PrerollPlayerPlacement.Superhero:
        return styles.superhero
      case PrerollPlayerPlacement.Packshot:
        return styles.packshot
    }
  }

  componentDidMount(): void {
    this.setState({ originalVolume: this.wrappers.videoPlayer.volume })
    this.wrappers.videoPlayer.preload = 'auto'
  }

  setUpPlay() {
    csm.count(`${PLAYER_COUNTER_PREFIX}::BUFFER::INITIALISED`)
    this.setState({ playing: true, hasPlayed: true, hidePlayer: false, paused: false }, () => {
      this.props.parentListeners && this.props.parentListeners.onPlayStart && this.props.parentListeners.onPlayStart()
    })
  }

  onPlayError(errorName: String) {
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play
    if (errorName === 'NotAllowedError') {
      fireDraperTrackingEvent(this.props.prerolls, 'playbackBlocked')
    } else {
      fireDraperTrackingEvent(this.props.prerolls, 'error')
    }
  }

  play() {
    if (this.wrappers.videoPlayer && this.wrappers.videoPlayer.readyState >= this.expectedReadyState) {
      const playPromise = this.wrappers.videoPlayer.play()
      if (typeof playPromise !== 'undefined') {
        playPromise.then(() => {
          this.setUpPlay()
          fireDraperTrackingEvent(this.props.prerolls, 'defaultImpression')
        }).catch((err) => {
          this.onPlayError(err)
          csm.logError(`Pre-Roll Player Failed to play: ${err}.`, err)
        })
      } else { // This is for IE11 and below as it does not support promises https://caniuse.com/#feat=promises
        try {
          this.wrappers.videoPlayer.play()
          this.setUpPlay()
          fireDraperTrackingEvent(this.props.prerolls, 'defaultImpression')
        } catch (err) {
          this.onPlayError(err)
          csm.logError(`Pre-Roll Player Failed to play: ${err}.`, err)
        }
      }
    }
  }

  onLoadError() {
    const err = this.wrappers.videoPlayer && this.wrappers.videoPlayer.error
    if (err) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
      // Justification in PVWebXPL-398
      if (err.code === MediaError.MEDIA_ERR_DECODE) {
        fireDraperTrackingEvent(this.props.prerolls, 'error')
      } else {
        fireDraperTrackingEvent(this.props.prerolls, 'playbackBlocked')
      }
    }

    csm.count(`${PLAYER_COUNTER_PREFIX}::loadError`)

    setTimeout(() => {
      this.stop()
    }, LOAD_ERROR_TIMEOUT)
  }

  onTimeUpdate() {
    const duration = this.wrappers.videoPlayer && this.wrappers.videoPlayer.duration,
      currentTime = this.wrappers.videoPlayer && this.wrappers.videoPlayer.currentTime
    if (!duration || !currentTime) {
      return
    }


    if (!this.thirdQuartileReached && currentTime >= duration * 0.75) {
      this.thirdQuartileReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'thirdQuartile')
    } else if (!this.midpointReached && currentTime >= duration * 0.5) {
      this.midpointReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'midpoint')
    } else if (!this.firstQuartileReached && currentTime >= duration * 0.25) {
      this.firstQuartileReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'firstQuartile')
    }

  }

  onPlaybackComplete() {
    fireDraperTrackingEvent(this.props.prerolls, 'complete')
    this.props.parentListeners && this.props.parentListeners.onPlayComplete && this.props.parentListeners.onPlayComplete()
    return this.stop()
  }

  stop() {
    this.setState({
      playing: false, hasPlayed: false, hidePlayer: true
    }, () => {
      this.props.parentListeners && this.props.parentListeners.onPlayStop && this.props.parentListeners.onPlayStop()

      this.wrappers.videoPlayer.pause()

      this.removeEventListeners()

      if (this.props.playerIsActive && this.props.videoContext.activeActivePlayer == this) {
        this.props.videoContext.updateContext({...this.props.videoContext, activeActivePlayer: null})
      }
    })
  }

  logMuteMetrics() {
    if (global.__DEVSERVER__) return
    if (this.props.videoContext.muted) {
      fireDraperTrackingEvent(this.props.prerolls, 'mute')
      fetch(this.props.prerolls.ipbTrackingEvents.mute)
    } else {
      fireDraperTrackingEvent(this.props.prerolls, 'unmute')
      fetch(this.props.prerolls.ipbTrackingEvents.unmute)
    }
  }

  toggleMute(event: MouseEvent) {
    event.preventDefault()
    this.props.videoContext.updateContext({...this.props.videoContext, muted: !this.props.videoContext.muted})
    this.logMuteMetrics()
  }


  onWaiting() {
    csm.count(`${PLAYER_COUNTER_PREFIX}::BUFFER::REINITIALISING`)
  }

  addEventListeners() {
    this.wrappers.videoPlayer.addEventListener('canplaythrough', this.playHandler)
    this.wrappers.videoPlayer.addEventListener('ended', this.stopHandler)
    this.wrappers.videoPlayer.addEventListener('timeupdate', this.updateHandler)
    this.wrappers.videoPlayer.addEventListener('waiting', this.waitingHandler)
    this.wrappers.videoPlayer.addEventListener('error', this.errorHandler)
  }

  removeEventListeners() {
    this.stopHandler &&
      this.wrappers.videoPlayer.removeEventListener('ended', this.stopHandler)
    this.playHandler &&
      this.wrappers.videoPlayer.removeEventListener('canplaythrough', this.playHandler)
    this.updateHandler &&
      this.wrappers.videoPlayer.removeEventListener('timeupdate', this.updateHandler)
    this.errorHandler &&
      this.wrappers.videoPlayer.removeEventListener('error', this.errorHandler)
    this.waitingHandler &&
      this.wrappers.videoPlayer.removeEventListener('waiting', this.waitingHandler)
    this.scrollHandler &&
      window.removeEventListener('scroll', this.scrollHandler)
  }

  componentWillUnmount() {
    this.removeEventListeners()
  }

  componentDidUpdate(prevProps: PrerollPlayerProps) {
    if (this.state.playing && this.props.videoContext != prevProps.videoContext) {
      if (this.props.playerIsActive) {
        if (this.props.videoContext.activeActivePlayer != this) {
          this.stop()
        }
      } else {
        if (this.props.videoContext.activeActivePlayer) {
          this.wrappers.videoPlayer && this.wrappers.videoPlayer.pause()
          this.setState({paused: true})
        } else {
          this.scrollHandler = () => {
            if (this.state.playing) {
              this.wrappers.videoPlayer && this.wrappers.videoPlayer.play()
              this.setState({paused: false})
              window.removeEventListener('scroll', this.scrollHandler)
            }
          }
          window.addEventListener('scroll', this.scrollHandler)
        }
      }
    }
  }

  startPlayer() {
    this.playHandler = () => this.play()
    this.stopHandler = () => this.onPlaybackComplete()
    this.updateHandler = () => this.onTimeUpdate()
    this.errorHandler = () => this.onLoadError()
    this.waitingHandler = () => this.onWaiting()
    this.addEventListeners()

    if (this.props.playerIsActive) {
      this.props.videoContext.updateContext({...this.props.videoContext, activeActivePlayer: this})
    }

    // Sometimes in Firefox when the video is in the cache of the browser
    // the canplaythrough event is fired before we have had a chance to
    // register the event
    if (this.wrappers.videoPlayer.readyState >= this.expectedReadyState) {
      if (this.props.placement === PrerollPlayerPlacement.Superhero) {
        this.wrappers.videoPlayer && this.wrappers.videoPlayer.load()
      }
      this.playHandler()
    }
  }

  render() {
    if (this.state.hasError || !this.props.prerolls || !this.props.prerolls.mediaFile || !this.props.videoContext) {
      return null
    }
    const showMuteToggle = !safeBool(this.props.prerolls.disableUnmute)
    return (
      <a
        className={cn(
          styles.preRollPlayer, this.styleFromPlacementType(),
          'tst-video-overlay-player-overlay', 'tst-preroll-player',
          {[styles.hide]: this.state.hidePlayer, [styles.show]: !this.state.hidePlayer}
        )}
        href={this.props.link} ref={
          (elem) => {
            this.wrappers.videoWrapper = elem
          }
        }>
        <video
          className={'tst-video-overlay-player-html5'}
          muted={this.props.videoContext.muted}
          src={this.props.prerolls.mediaFile} ref={
            (elem) => {
              this.wrappers.videoPlayer = elem
            }}
        />
        {this.props.placement === PrerollPlayerPlacement.Superhero && <div className={styles.videoFadeOverlay} />}
        {showMuteToggle && this.props.videoContext.muted && <a onMouseDown={this.toggleMute.bind(this)}
          className={cn(styles.mutingAction, 'tst-video-overlay-player-muted')}>{mutedButton}</a>}
        {showMuteToggle && !this.props.videoContext.muted && <a onMouseDown={this.toggleMute.bind(this)}
          className={cn(styles.mutingAction, 'tst-video-overlay-player-unmuted')}>{unmutedButton}</a>}
      </a>

    )
  }
}
